import React from 'react';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { StripePaymentFrame } from '../orderPage/steps/payments/StripePaymentFrame';
import { SquareFrameVariant, SquarePaymentFrame } from '../orderPage/steps/payments/SquarePaymentFrame';
import { parseSquarePaymentForm } from '../orderPage/steps/payments/squarePaymentForm';
import { AccountIframeSettingsData } from '../api';
import { useLanguage } from '../utils/GeneralUtils';

const urlHeightResolver = (
  paymentUrl: string,
  isSmallScreen: boolean,
  paymentIframeSettings?: AccountIframeSettingsData,
) => {
  if (paymentIframeSettings?.desktop_height && !isSmallScreen) {
    return paymentIframeSettings.desktop_height;
  }
  if (paymentIframeSettings?.mobile_height && isSmallScreen) {
    return paymentIframeSettings.mobile_height;
  }
  if (paymentUrl.includes('payplus')) {
    return '710px';
  }
  if (paymentUrl.includes('payme')) {
    return isSmallScreen ? '700px' : '440px';
  }
  if (paymentUrl.includes('meshulam')) {
    return isSmallScreen ? '363px' : '480px';
  }
  if (paymentUrl.includes('tranzila')) {
    return isSmallScreen ? '600px' : '500px';
  }
  return '700px';
};

type PassPaymentIframeProps = {
  paymentUrl: string;
  stripeAccountId?: string;
  stripe_integration_publishable_key?: string;
  paymentIframeSettings?: AccountIframeSettingsData;
  businessName?: string;
  payLabelKey?: string;
  nextChargeDate?: string;
  onFormExpired: () => void;
};

export const PassPaymentIframe = ({
  paymentUrl,
  stripe_integration_publishable_key,
  stripeAccountId,
  paymentIframeSettings,
  businessName,
  payLabelKey,
  nextChargeDate,
  onFormExpired,
}: PassPaymentIframeProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();

  const squarePaymentForm = parseSquarePaymentForm(paymentUrl);
  if (squarePaymentForm) {
    return (
      <SquarePaymentFrame
        form={squarePaymentForm}
        language={language}
        variant={SquareFrameVariant.PORTAL}
        businessName={businessName}
        payLabelKey={payLabelKey}
        nextChargeDate={nextChargeDate}
        onPaid={(redirectUrl) => {
          window.location.href = redirectUrl;
        }}
        onFormExpired={onFormExpired}
      />
    );
  }

  if (stripe_integration_publishable_key) {
    return (
      <StripePaymentFrame
        publishableKey={stripe_integration_publishable_key}
        clientSecret={paymentUrl}
        stripeAccountId={stripeAccountId}
      />
    );
  }

  const note = paymentIframeSettings?.note?.[language];

  return (
    <Grid container flexDirection="column">
      {note && (
        <Grid item mt={3}>
          <Typography fontWeight={700}>{note}</Typography>
        </Grid>
      )}
      <iframe
        style={{
          borderRadius: '8px',
          border: `1px solid ${theme.palette.primary.main}`,
          backgroundColor: 'white',
        }}
        height={urlHeightResolver(paymentUrl, isSmallScreen, paymentIframeSettings)}
        title="payment-iframe"
        src={paymentUrl}
        className="payment-iframe"
      />
    </Grid>
  );
};

export default PassPaymentIframe;
